import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdChannelsOverviewInfoComponent } from './hd-channels-overview-info.component';
import { HdCircuitInfoFullComponent } from '../circuit-info/hd-circuit-info-full.component';
import { HdCircuitsOverviewInfoComponent } from '../circuit-info/hd-circuits-overview-info.component';
import { HdCircuitInfoContext, HdCircuitsOverviewInfoContext } from '../circuit-info/hd-circuit-info-context.model';
import { HdChannelInfoService } from '../hd-channel-info.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdStreamInfoService } from '../hd-stream-info.service';
import { HdDocPanelService } from './hd-doc-panel.service';
import { HdDocGateOpenerService } from './hd-doc-gate-opener.service';
import { HdDocRef } from './hd-doc-ref.model';
import { resolveDocLink } from './hd-doc-link.resolver';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { DOCK_RENDER_ZONE } from '@shared/floating-panel/dock-render-zone.token';
import { FLOATING_PANEL_HEADER_ACTIONS, FloatingPanelHeaderAction } from '@shared/floating-panel/floating-panel-header-actions.token';

/**
 * Gedockt doc-viewer paneel. Toont één {@link HdDocRef} tegelijk en houdt een
 * **eigen history-stack per paneel-instance** bij: klikken op een markdown-link
 * (in `same-panel`-modus) vervangt de inhoud en toont terug-knoppen boven- én
 * onderaan; meerdere niveaus diep terug is mogelijk. In `new-panel`-modus opent
 * een link een apart paneel via {@link HdDocPanelService}.
 *
 * Link-interceptie gebeurt via één gedelegeerde click-handler op de container:
 * markdown-links van de ingebedde circuit-/overzicht-componenten én van de
 * generieke `<markdown>` zijn allemaal descendants, dus we hoeven die
 * componenten niet aan te passen. Externe/onbekende links behouden hun
 * standaard browsergedrag (nieuw tabblad).
 */
@Component({
  selector: 'hd-doc-viewer',
  templateUrl: './hd-doc-viewer.component.html',
  styleUrl: './hd-doc-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MarkdownComponent,
    TranslocoModule,
    HdChannelsOverviewInfoComponent,
    HdCircuitInfoFullComponent,
    HdCircuitsOverviewInfoComponent,
  ],
})
export class HdDocViewerComponent {
  private readonly docPanel = inject(HdDocPanelService);
  private readonly channelInfo = inject(HdChannelInfoService);
  private readonly contentLang = inject(HdContentLanguageService, { optional: true });
  private readonly streamInfo = inject(HdStreamInfoService);
  private readonly gateOpener = inject(HdDocGateOpenerService);
  /** Enkel aanwezig wanneer gemount in een (gedockt) floating panel. */
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly transloco = inject(TranslocoService);
  /** Aanwezig in gedockte modus → dan is het paneel niet zwevend. */
  private readonly dockRenderZone = inject(DOCK_RENDER_ZONE, { optional: true });
  /** Header-icoonknoppen — enkel geleverd in zwevende modus. */
  private readonly headerActionsSignal = inject(FLOATING_PANEL_HEADER_ACTIONS, { optional: true });
  /** Initiële doc-ref; door {@link HdDocViewerPanelComponent} uit FLOATING_PANEL_DATA gezet. */
  readonly initialRef = input.required<HdDocRef>();

  /**
   * Embedded viewers renderen en navigeren documenten zonder het omringende
   * floating panel over te nemen (docklabel, headeractions of dialog-breedte).
   */
  readonly embedded = input(false);

  /** Alleen een zelfstandige viewer zonder dock-zone beheert floating layout. */
  protected readonly isFloating = computed(() => !this.embedded() && this.dockRenderZone == null);

  /**
   * Maximaal aantal entries in de back-stack (per paneel-instance). Diep
   * doorklikken via doc-links laat de stack anders onbeperkt groeien; boven deze
   * grens vallen de oudste entries weg (ruim genoeg: 30 stappen terug volstaat
   * ver voor de UX, en de stack wordt sowieso opgeruimd wanneer het paneel sluit).
   */
  private static readonly HISTORY_LIMIT = 30;

  /** Back-stack per paneel-instance: `history[index]` is de zichtbare entry. */
  private readonly history = signal<readonly HdDocRef[]>([]);
  private readonly index = signal(0);
  private seeded = false;

  constructor() {
    // Seed de history één keer uit de initiële ref. Latere FLOATING_PANEL_DATA-
    // wissels (bv. een her-focus met dezelfde key) mogen de history niet wissen.
    effect(() => {
      const ref = this.initialRef();
      if (this.seeded) return;
      this.seeded = true;
      this.history.set([ref]);
      this.index.set(0);
    });

    // Houd de dock-tab-label in sync met de zichtbare entry zodat de tab
    // meebeweegt terwijl je door de docs navigeert.
    effect(() => {
      if (this.embedded()) return;
      const ref = this.current();
      this.panelRef?.setDockTabLabel(ref ? this.docPanel.docTabLabel(ref) : '');
    });

    // Grootte-toggle (breder/smaller) als header-actie — enkel in zwevende
    // modus (gedockt is er geen header-strook). Spiegelt de gate-info toggle.
    effect(() => {
      if (this.embedded()) return;
      const actions = this.headerActionsSignal;
      if (!actions) return;
      const lang = this.transloco.getActiveLang();
      const wide = this.docPanel.dialogSize() === 'wide';
      const action: FloatingPanelHeaderAction = {
        icon: wide ? 'down-left-and-up-right-to-center' : 'up-right-and-down-left-from-center',
        tooltip: this.transloco.translate(
          wide
            ? 'humanDesign.chart.configurator.docPanel.narrow'
            : 'humanDesign.chart.configurator.docPanel.widen',
          undefined,
          lang,
        ),
        onClick: () => this.toggleSize(),
      };
      actions.set([action]);
    });
  }

  protected readonly current = computed<HdDocRef | null>(() => this.history()[this.index()] ?? null);
  protected readonly canGoBack = computed(() => this.index() > 0);

  /** Label van de entry waar "terug" naartoe gaat (voor de terug-knop). */
  protected readonly previousLabel = computed<string>(() => {
    const prev = this.history()[this.index() - 1];
    return prev ? this.docPanel.docTabLabel(prev) : '';
  });
  protected readonly currentKind = computed<HdDocRef['kind'] | null>(() => this.current()?.kind ?? null);

  /** Zwevende dialog in "wide"-modus (past de bredere breedte toe). */
  protected readonly isWide = computed(() => this.isFloating() && this.docPanel.dialogSize() === 'wide');

  protected readonly circuitContext = computed<HdCircuitInfoContext | null>(() => {
    const ref = this.current();
    return ref?.kind === 'circuit' ? { circuitKey: ref.circuitKey, variant: ref.variant } : null;
  });

  protected readonly overviewContext: HdCircuitsOverviewInfoContext = { variant: 'normal' };

  /** URL van de te renderen markdown voor `stream`- en `markdown`-entries. */
  protected readonly markdownUrl = computed<string | null>(() => {
    const ref = this.current();
    if (ref?.kind === 'channel') return this.channelInfo.channelMarkdownUrl(ref.channelKey);
    if (ref?.kind === 'stream') return this.streamInfo.streamMarkdownUrl(ref.streamKey);
    if (ref?.kind === 'markdown') return ref.url;
    return null;
  });

  goBack(): void {
    if (this.index() > 0) this.index.update(value => value - 1);
  }

  /** "Alle kanalen"-blok onder een kanaaldocument → het per-type-overzicht. */
  protected openAllChannels(): void {
    this.follow({ kind: 'channels-overview' });
  }

  /**
   * Label van het "Alle kanalen"-blok, content-taal-reactief. Bewust geen
   * transloco-pipe: host-panelen (kanaal-info) stubben TranslocoService in hun
   * specs minimaal, en de pipe crasht dan op de ontbrekende config; de
   * services zijn hier om dezelfde reden optioneel gechaind.
   */
  protected readonly allChannelsLabel = computed(() => {
    this.contentLang?.translationsLoadedTick?.();
    const lang = this.contentLang?.effectiveLang?.();
    return this.transloco.translate<string>('humanDesign.chart.channelInfo.allChannels.link', undefined, lang);
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private toggleSize(): void {
    const willWiden = this.docPanel.dialogSize() === 'normal';
    this.docPanel.toggleDialogSize();
    // Breder → hercentreer zodat het paneel niet buiten beeld valt (mirror van
    // gate-info expand). Bij versmallen is dat niet nodig.
    if (willWiden) this.panelRef?.recenter();
  }

  /**
   * Klik ergens in de content: eerst een poort-blokje van de kanaal-documentkop
   * (opent de poort-detailinfo van de actieve chart), anders een `<a>`-link naar
   * een interne doc onderscheppen.
   */
  protected onContentClick(event: MouseEvent): void {
    if (this.tryOpenChannelGate(event.target, event)) return;
    this.handleAnchorActivation(event.target, event);
  }

  /** Enter op een gefocuste markdown-link (de links dragen role=link/tabindex). */
  protected onContentKeydown(event: KeyboardEvent): void {
    if (this.tryOpenChannelGate(event.target, event)) return;
    this.handleAnchorActivation(event.target, event);
  }

  /**
   * Klik/Enter op een van de twee poort-blokjes bovenaan een kanaaldocument
   * (`.hd-channel-gate`): open de poort-detailinfo via de door de chart-pagina
   * geregistreerde opener. Doet niets wanneer er geen chart actief is (geen
   * opener) of buiten een blokje geklikt wordt.
   */
  private tryOpenChannelGate(target: EventTarget | null, event: Event): boolean {
    const open = this.gateOpener.openGate();
    if (!open) return false;
    const block = (target as HTMLElement | null)?.closest?.('.hd-channel-gate') as HTMLElement | null;
    if (!block) return false;
    const gate = this.parseGateFromBlock(block);
    if (gate == null) return false;
    event.preventDefault();
    event.stopPropagation();
    open(gate);
    return true;
  }

  /** Leest het poortnummer uit de titel van een `.hd-channel-gate`-blok ("45: …"). */
  private parseGateFromBlock(block: HTMLElement): number | null {
    const titleText = block.querySelector('.hd-channel-gate__title')?.textContent ?? '';
    const match = titleText.match(/\d+/);
    if (!match) return null;
    const gate = Number.parseInt(match[0], 10);
    return Number.isFinite(gate) ? gate : null;
  }

  private handleAnchorActivation(target: EventTarget | null, event: Event): void {
    const anchor = (target as HTMLElement | null)?.closest?.('a') as HTMLAnchorElement | null;
    if (!anchor) return;
    const ref = resolveDocLink(anchor.getAttribute('href'));
    if (!ref) return; // externe / onbekende link → standaard browsergedrag.
    event.preventDefault();
    event.stopPropagation();
    this.follow(ref);
  }

  private follow(ref: HdDocRef): void {
    if (this.docPanel.linkOpenMode() === 'new-panel') {
      this.docPanel.openDocPanel(ref);
      return;
    }
    // same-panel: kap een eventuele voorwaartse tak af, push de nieuwe entry
    // en schuif de index door zodat "terug" naar de vorige inhoud gaat.
    let stack = [...this.history().slice(0, this.index() + 1), ref];
    // Begrens de back-stack: laat de oudste entries vallen zodat het geheugen
    // niet onbeperkt groeit bij diep doorklikken (zie HISTORY_LIMIT).
    if (stack.length > HdDocViewerComponent.HISTORY_LIMIT) {
      stack = stack.slice(stack.length - HdDocViewerComponent.HISTORY_LIMIT);
    }
    this.history.set(stack);
    this.index.set(stack.length - 1);
  }
}
